/**
 * Pure decision logic extracted from `BrazeIdentity` so its useEffect
 * stays simple and the branching is trivially unit-testable (keeping
 * the component's CRAP score under the Fallow threshold).
 *
 * Returns either a `skip` (no Braze SDK call) or `identify` action
 * (call `brazeIdentify` with the resolved attribute payload).
 */

export type IdentifyAttrs = {
  email: string | undefined;
  first_name: string | undefined;
  last_name: string | undefined;
};

export type IdentifyAction =
  | { kind: 'skip' }
  | { kind: 'identify'; customerId: string; attrs: IdentifyAttrs };

export type ResolveIdentifyInput = {
  consented: boolean;
  customerId: string | null | undefined;
  lastIdentifiedId: string | null;
  customerEmail: string | null | undefined;
  customerFirstName: string | null | undefined;
  customerLastName: string | null | undefined;
};

/**
 * Decides whether to call `brazeIdentify` for the current customer.
 *
 * Skip when:
 *  - the user has not granted marketing consent
 *  - there is no customer id (anonymous visitor)
 *  - we have already identified this customer id (dedup via ref)
 *
 * Otherwise return an `identify` action with the attribute payload —
 * `null` props are normalised to `undefined` so they're omitted from
 * the JSON body rather than overwriting any pre-existing value.
 */
export const resolveIdentifyAction = ({
  consented,
  customerId,
  lastIdentifiedId,
  customerEmail,
  customerFirstName,
  customerLastName,
}: ResolveIdentifyInput): IdentifyAction => {
  if (!consented || !customerId) {
    return { kind: 'skip' };
  }

  if (customerId === lastIdentifiedId) {
    return { kind: 'skip' };
  }

  return {
    kind: 'identify',
    customerId,
    attrs: {
      email: customerEmail ?? undefined,
      first_name: customerFirstName ?? undefined,
      last_name: customerLastName ?? undefined,
    },
  };
};
